import React, { useState } from 'react'
import {
    EuiFormRow,
    EuiFieldText,
    EuiTextArea,
    EuiFieldNumber,
    EuiFlexGroup,
    EuiFlexItem,
    EuiTitle,
    EuiSpacer,
    EuiPanel,
    EuiButton,
    EuiButtonEmpty,
    EuiText,
    EuiHorizontalRule
} from '@elastic/eui'

import { PersonaDemographics } from 'api/interfaces/Persona'
import { SegmentTarget } from 'api/interfaces/Campaign'

export interface PersonaFormFields {
    name: string
    description: string
    demographics: PersonaDemographics
    triggers: string[]
    barriers: string[]
    rank: number
    prioritizationReason: string
    segments: SegmentTarget[]
}

export interface PersonaFormProps {
    personaFormFields: PersonaFormFields
    setPersonaFormFields: (fields: PersonaFormFields) => void
}

export const PersonaForm: React.FC<PersonaFormProps> = ({ personaFormFields, setPersonaFormFields }) => {
    const [newTrigger, setNewTrigger] = useState('')
    const [newBarrier, setNewBarrier] = useState('')
    const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())

    const markFieldAsTouched = (fieldName: string) => {
        setTouchedFields(prev => new Set([...prev, fieldName]))
    }

    const isFieldInvalid = (fieldName: string, isInvalid: boolean) => {
        return touchedFields.has(fieldName) && isInvalid
    }

    const updateField = (field: keyof PersonaFormFields, value: any) => {
        setPersonaFormFields({
            ...personaFormFields,
            [field]: value
        })
    }

    const updateDemographic = (field: keyof PersonaDemographics, value: string) => {
        setPersonaFormFields({
            ...personaFormFields,
            demographics: {
                ...personaFormFields.demographics,
                [field]: value
            }
        })
    }

    const addTrigger = () => {
        if (newTrigger.trim()) {
            updateField('triggers', [...personaFormFields.triggers, newTrigger.trim()])
            setNewTrigger('')
        }
    }

    const removeTrigger = (index: number) => {
        const updatedTriggers = personaFormFields.triggers.filter((_, i) => i !== index)
        updateField('triggers', updatedTriggers)
    }

    const addBarrier = () => {
        if (newBarrier.trim()) {
            updateField('barriers', [...personaFormFields.barriers, newBarrier.trim()])
            setNewBarrier('')
        }
    }

    const removeBarrier = (index: number) => {
        const updatedBarriers = personaFormFields.barriers.filter((_, i) => i !== index)
        updateField('barriers', updatedBarriers)
    }

    return (
        <>
            <EuiTitle size="s">
                <h3>Basic Information</h3>
            </EuiTitle>
            <EuiSpacer size="m" />

            <EuiFormRow fullWidth label="Persona Name *" isInvalid={isFieldInvalid('name', !personaFormFields.name.trim())}>
                <EuiFieldText
                    fullWidth
                    value={personaFormFields.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    onBlur={() => markFieldAsTouched('name')}
                    placeholder="Enter persona name"
                />
            </EuiFormRow>

            <EuiFormRow fullWidth label="Description *" isInvalid={isFieldInvalid('description', !personaFormFields.description.trim())}>
                <EuiTextArea
                    fullWidth
                    value={personaFormFields.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    onBlur={() => markFieldAsTouched('description')}
                    placeholder="Describe this persona in detail"
                    rows={12}
                />
            </EuiFormRow>

            <EuiSpacer size="l" />
            <EuiHorizontalRule />

            <EuiTitle size="s">
                <h3>Demographics</h3>
            </EuiTitle>
            <EuiSpacer size="m" />

            <EuiFlexGroup gutterSize="m">
                <EuiFlexItem>
                    <EuiFormRow fullWidth label="Age Range *" isInvalid={isFieldInvalid('demographics.ageRange', !personaFormFields.demographics.ageRange.trim())}>
                        <EuiFieldText
                            fullWidth
                            value={personaFormFields.demographics.ageRange}
                            onChange={(e) => updateDemographic('ageRange', e.target.value)}
                            onBlur={() => markFieldAsTouched('demographics.ageRange')}
                            placeholder="e.g., 25-45"
                        />
                    </EuiFormRow>
                </EuiFlexItem>
                <EuiFlexItem>
                    <EuiFormRow fullWidth label="Gender *" isInvalid={isFieldInvalid('demographics.gender', !personaFormFields.demographics.gender.trim())}>
                        <EuiFieldText
                            fullWidth
                            value={personaFormFields.demographics.gender}
                            onChange={(e) => updateDemographic('gender', e.target.value)}
                            onBlur={() => markFieldAsTouched('demographics.gender')}
                            placeholder="e.g., All, Male, Female"
                        />
                    </EuiFormRow>
                </EuiFlexItem>
            </EuiFlexGroup>

            <EuiFlexGroup gutterSize="m">
                <EuiFlexItem>
                    <EuiFormRow fullWidth label="Income Range *" isInvalid={isFieldInvalid('demographics.incomeRange', !personaFormFields.demographics.incomeRange.trim())}>
                        <EuiFieldText
                            fullWidth
                            value={personaFormFields.demographics.incomeRange}
                            onChange={(e) => updateDemographic('incomeRange', e.target.value)}
                            onBlur={() => markFieldAsTouched('demographics.incomeRange')}
                            placeholder="e.g., $50K-$100K"
                        />
                    </EuiFormRow>
                </EuiFlexItem>
                <EuiFlexItem>
                    <EuiFormRow fullWidth label="Education *" isInvalid={isFieldInvalid('demographics.education', !personaFormFields.demographics.education.trim())}>
                        <EuiFieldText
                            fullWidth
                            value={personaFormFields.demographics.education}
                            onChange={(e) => updateDemographic('education', e.target.value)}
                            onBlur={() => markFieldAsTouched('demographics.education')}
                            placeholder="e.g., College Graduate"
                        />
                    </EuiFormRow>
                </EuiFlexItem>
            </EuiFlexGroup>

            <EuiFlexGroup gutterSize="m">
                <EuiFlexItem>
                    <EuiFormRow fullWidth label="Occupation *" isInvalid={isFieldInvalid('demographics.occupation', !personaFormFields.demographics.occupation.trim())}>
                        <EuiFieldText
                            fullWidth
                            value={personaFormFields.demographics.occupation}
                            onChange={(e) => updateDemographic('occupation', e.target.value)}
                            onBlur={() => markFieldAsTouched('demographics.occupation')}
                            placeholder="e.g., Professional, Manager"
                        />
                    </EuiFormRow>
                </EuiFlexItem>
                <EuiFlexItem>
                    <EuiFormRow fullWidth label="Life Stage *" isInvalid={isFieldInvalid('demographics.lifestage', !personaFormFields.demographics.lifestage.trim())}>
                        <EuiFieldText
                            fullWidth
                            value={personaFormFields.demographics.lifestage}
                            onChange={(e) => updateDemographic('lifestage', e.target.value)}
                            onBlur={() => markFieldAsTouched('demographics.lifestage')}
                            placeholder="e.g., Young Family, Empty Nesters"
                        />
                    </EuiFormRow>
                </EuiFlexItem>
            </EuiFlexGroup>

            <EuiFlexGroup gutterSize="m">
                <EuiFlexItem>
                    <EuiFormRow fullWidth label="Home Value *" isInvalid={isFieldInvalid('demographics.homeValue', !personaFormFields.demographics.homeValue.trim())}>
                        <EuiFieldText
                            fullWidth
                            value={personaFormFields.demographics.homeValue}
                            onChange={(e) => updateDemographic('homeValue', e.target.value)}
                            onBlur={() => markFieldAsTouched('demographics.homeValue')}
                            placeholder="e.g., $200K-$500K"
                        />
                    </EuiFormRow>
                </EuiFlexItem>
                <EuiFlexItem />
            </EuiFlexGroup>

            <EuiSpacer size="l" />
            <EuiHorizontalRule />

            <EuiTitle size="s">
                <h3>Buying Triggers *</h3>
            </EuiTitle>
            <EuiSpacer size="s" />
            <EuiText size="xs" color="subdued">
                <p>Add at least one buying trigger that motivates this persona to purchase</p>
            </EuiText>
            <EuiSpacer size="m" />

            <EuiFlexGroup gutterSize="s">
                <EuiFlexItem>
                    <EuiFieldText
                        value={newTrigger}
                        onChange={(e) => setNewTrigger(e.target.value)}
                        placeholder="Add a buying trigger"
                        isInvalid={isFieldInvalid('triggers', personaFormFields.triggers.length === 0)}
                        onBlur={() => markFieldAsTouched('triggers')}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                addTrigger()
                            }
                        }}
                    />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                    <EuiButton onClick={addTrigger} size="s">
                        Add
                    </EuiButton>
                </EuiFlexItem>
            </EuiFlexGroup>

            {personaFormFields.triggers.length > 0 && (
                <>
                    <EuiSpacer size="s" />
                    <EuiPanel paddingSize="s">
                        {personaFormFields.triggers.map((trigger, index) => (
                            <div key={index}>
                                <EuiFlexGroup alignItems="center" gutterSize="s">
                                    <EuiFlexItem>
                                        <EuiText size="s">• {trigger}</EuiText>
                                    </EuiFlexItem>
                                    <EuiFlexItem grow={false}>
                                        <EuiButtonEmpty
                                            size="xs"
                                            color="danger"
                                            onClick={() => removeTrigger(index)}
                                        >
                                            Remove
                                        </EuiButtonEmpty>
                                    </EuiFlexItem>
                                </EuiFlexGroup>
                                {index < personaFormFields.triggers.length - 1 && <EuiSpacer size="xs" />}
                            </div>
                        ))}
                    </EuiPanel>
                </>
            )}

            <EuiSpacer size="l" />
            <EuiHorizontalRule />

            {/* Barriers */}
            <EuiTitle size="s">
                <h3>Barriers to Purchase *</h3>
            </EuiTitle>
            <EuiSpacer size="s" />
            <EuiText size="xs" color="subdued">
                <p>Add at least one barrier that prevents this persona from purchasing</p>
            </EuiText>
            <EuiSpacer size="m" />

            <EuiFlexGroup gutterSize="s">
                <EuiFlexItem>
                    <EuiFieldText
                        value={newBarrier}
                        onChange={(e) => setNewBarrier(e.target.value)}
                        placeholder="Add a barrier to purchase"
                        isInvalid={isFieldInvalid('barriers', personaFormFields.barriers.length === 0)}
                        onBlur={() => markFieldAsTouched('barriers')}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                addBarrier()
                            }
                        }}
                    />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                    <EuiButton onClick={addBarrier} size="s">
                        Add
                    </EuiButton>
                </EuiFlexItem>
            </EuiFlexGroup>

            {personaFormFields.barriers.length > 0 && (
                <>
                    <EuiSpacer size="s" />
                    <EuiPanel paddingSize="s">
                        {personaFormFields.barriers.map((barrier, index) => (
                            <div key={index}>
                                <EuiFlexGroup alignItems="center" gutterSize="s">
                                    <EuiFlexItem>
                                        <EuiText size="s">• {barrier}</EuiText>
                                    </EuiFlexItem>
                                    <EuiFlexItem grow={false}>
                                        <EuiButtonEmpty
                                            size="xs"
                                            color="danger"
                                            onClick={() => removeBarrier(index)}
                                        >
                                            Remove
                                        </EuiButtonEmpty>
                                    </EuiFlexItem>
                                </EuiFlexGroup>
                                {index < personaFormFields.barriers.length - 1 && <EuiSpacer size="xs" />}
                            </div>
                        ))}
                    </EuiPanel>
                </>
            )}
        </>
    )
} 